import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

export interface PeriodStats {
  startDate: string | null;
  endDate: string | null;
  arrived: number;
  cancelled: number;
  noShow: number;
  planned: number;
  total: number;
  totalPeople: number;
  totalChildren: number;
  arrivalRate: number;
}

export interface Comparison {
  reservationsDiff: number;
  reservationsDiffPercent: number;
  peopleDiff: number;
  peopleDiffPercent: number;
  cancelledDiff: number;
  arrivedDiff: number;
  arrivalRateDiff: number;
}

export interface DayOfWeekData {
  day: string;
  dayIndex: number;
  count: number;
  people: number;
}

export interface MonthlyDataItem {
  month: string;
  label: string;
  arrived: number;
  arrivedPeople: number;
  cancelled: number;
  planned: number;
  total: number;
  arrivalRate: number;
}

export interface StatusDistributionItem {
  name: string;
  value: number;
  color: string;
}

export interface SourceDistributionItem {
  source: string;
  count: number;
  people: number;
  arrived: number;
  arrivalRate: number;
}

export interface StatisticsData {
  period: PeriodStats;
  comparison: Comparison;
  byDayOfWeek: DayOfWeekData[];
  bySource: SourceDistributionItem[];
  monthlyData: MonthlyDataItem[];
  statusDistribution: StatusDistributionItem[];
}

interface UseStatisticsDataParams {
  startDate: Date;
  endDate: Date;
}

export function useStatisticsData({ startDate, endDate }: UseStatisticsDataParams) {
  const [data, setData] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Non authentifié");
      }

      const formattedStart = format(startDate, 'yyyy-MM-dd');
      const formattedEnd = format(endDate, 'yyyy-MM-dd');

      const { data: responseData, error: invokeError } = await supabase.functions.invoke('get-statistics', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        },
        body: {
          startDate: formattedStart,
          endDate: formattedEnd
        }
      });

      if (invokeError) {
        throw new Error(invokeError.message || 'Erreur serveur');
      }

      setData(responseData);
    } catch (err: any) {
      console.error("Error fetching statistics:", err);
      setError(err.message || "Erreur lors du chargement des statistiques");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate.toISOString(), endDate.toISOString()]);

  return { data, loading, error, refetch: fetchData };
}
